import type { AuditEntry, AuditKind, AuditOrigin } from './audit';

/**
 * The place a sign-in's origin resolves to, as one line — "City, Country", or
 * just the part we have, or '' when the dataset could not place the address. The
 * UI adds the "(approximate)" qualifier; this returns only the words, so the
 * same helper serves the drill and the exported file. Kept here with the other
 * trail-rendering helpers (and away from audit.ts, which reaches env) so the
 * reader and its tests load it without the rest of the model.
 */
export function originPlace(o: AuditOrigin): string {
  return [o.city, o.country].map((p) => p.trim()).filter(Boolean).join(', ');
}

/**
 * MAKING A TRAIL READABLE WITHOUT MAKING IT LESS TRUE — #306.
 *
 * WHAT WAS WRONG. Counted on the test copy on 29 August 2026 rather than
 * guessed at: of 1,172 entries, 811 are `project` and **440 of them say
 * "changed who signs work off here"**. Signatories is a list, and every save of
 * that box writes a row, so one edit made in four small steps becomes four
 * lines. Nothing is wrong with any single row. Together they bury everything
 * worth reading — which for an audit trail is the same as not having one.
 *
 * ── FOLDED WHEN IT IS READ, NEVER WHEN IT IS WRITTEN ────────────────────────
 *
 * This is the whole design decision and it was not a close call.
 *
 * Writing fewer rows would fix the screen and break the record. 0030 and 0068
 * put the writing in the database precisely so it cannot be skipped — "a
 * history somebody can refuse to write is not a history" — and the audit table
 * carries a select policy and an insert policy and no others, so that nothing,
 * including an admin, can change or remove an entry. Collapsing at write time
 * would be this code deciding which of those rows deserved to exist, which is
 * the same power with a friendlier name.
 *
 * So every row is still written, still kept, and still exported. What changes
 * is how many LINES a reader is shown, and every folded line says how many
 * entries stand behind it and can be opened to show them.
 */

/**
 * How close together two edits have to be to count as one piece of work.
 *
 * TEN MINUTES, and the number matters less than the shape of the mistake at
 * each end. Too short and a person picking their way through a signatory list
 * still fills the screen, which is the fault this exists to fix. Too long and
 * two genuinely separate decisions — the same field, changed in the morning and
 * changed back after lunch — are shown as one, and an audit trail that hides a
 * reversal is worse than a noisy one.
 */
export const SAME_PIECE_OF_WORK = 10 * 60 * 1000;

/**
 * ── AND THE SAME EVENT AGAIN IS BOUNDED BY THE DAY, NOT BY TEN MINUTES ──────
 *
 * Ten minutes is the right window for somebody picking their way through a
 * field. It is the wrong one for "this happened again", and the numbers say so
 * rather than the argument: counted on the test copy on 1 September 2026, the
 * audit table holds 505 security entries, 101 of which are the same sentence —
 * `signed in from the live board`. A ten-minute window folds 90 of the 505,
 * because sign-ins are spread across days rather than bunched into bursts. That
 * is an 18% improvement to a screen Vinnie had already called unreadable, which
 * is the same as no improvement.
 *
 * A CALENDAR DAY, DELIBERATELY, AND NOT A LONGER ROLLING WINDOW. A rolling
 * window chains: entries every six hours inside a twelve-hour window join into
 * one run that never ends, and a line reading "312 times" spanning three months
 * is not a reading aid. A day cannot chain, and it is the unit the reader is
 * already thinking in.
 *
 * ⚠️ WHAT THIS COSTS, SAID PLAINLY. A sign-in at three in the morning no longer
 * has a line to itself; it is inside the day's count. That is a real loss and it
 * is bought with a real gain — nobody scans 101 identical lines, so the 3am one
 * was already invisible, and now it is one click from the top of the list with
 * its own time beside it. The entries are all still there, all still exported,
 * and `asFile` has never seen a folded line.
 */
const sameDay = (a: number, b: number): boolean =>
  new Date(a).toDateString() === new Date(b).toDateString();

/**
 * The part of a sentence that names WHICH field moved.
 *
 * Every settings line is written as `changed <label> from <a> to <b>` by
 * `describeSettings`, so the label is what makes two entries the same subject.
 * Anything that does not match that shape has no subject and is never folded —
 * which is the safe direction: an unrecognised sentence is shown as itself.
 */
const subjectOf = (what: string): string | null =>
  /^changed (.+?) from .+ to .+$/.exec(what)?.[1] ?? null;

/**
 * WHAT MAKES TWO ENTRIES THE SAME THING HAPPENING AGAIN.
 *
 * Two shapes, and the difference between them is the whole of this file's
 * caution:
 *
 *   · `changed <label> from <a> to <b>` — the SUBJECT is the label, and the
 *     values are expected to differ. That is a person picking their way
 *     through one field, and the fold says where it ended up.
 *
 *   · anything else — the subject is the WHOLE SENTENCE, so only entries that
 *     say exactly the same thing fold together. `signed in from Port of Spain`
 *     and `signed in from London` ten minutes apart are two different facts and
 *     one of them is alarming; nothing here will ever show them as one line.
 *
 * ── WHY THE SECOND SHAPE EXISTS AT ALL, 1 SEPTEMBER 2026 ────────────────────
 *
 * It did not, and that was deliberate: an unrecognised sentence stood for
 * itself, on the grounds that a half-understood line rewritten with confidence
 * is worse than the original. That reasoning still holds and is why this counts
 * identical sentences rather than summarising differing ones — nothing is
 * rewritten here, only counted.
 *
 * What changed is that the noise moved. `record_session_started` (0068) fires
 * on insert AND on every update of `signed_in_at`, so one browser coming back
 * writes bursts of identical sign-in rows minutes apart, and History filled
 * with them. Vinnie, 1 September 2026: *"The grouping of events on History page
 * is not grouped and does not work as I expected it to"*, and then, deciding
 * it: *"Security events should be folded into one ine as well"*.
 *
 * ⚠️ THE WINDOW IS NOT WIDENED FOR THESE, and that is the guard. Ten minutes
 * of identical sign-ins is one arrival. Two sign-ins hours apart are two
 * arrivals, and one of them may be at three in the morning — a trail that hides
 * that is worse than a noisy one, which is the same argument the reversal case
 * is built on.
 */
const sameThingOf = (what: string): string => subjectOf(what) ?? what;

const endsOf = (what: string): { from: string; to: string } | null => {
  const m = /^changed .+? from (.+) to (.+)$/.exec(what);
  return m ? { from: m[1], to: m[2] } : null;
};

export interface Folded {
  /** The newest entry in the run, and the one the row is drawn from. */
  entry: AuditEntry;
  /** What the line should say. The net change when it can be worked out. */
  what: string;
  /** How many entries this line stands for. One means it stands for itself. */
  times: number;
  /** All of them, newest first. Length always matches `times`. */
  behind: AuditEntry[];
  /** True when the field ended up where it started. */
  wentNowhere: boolean;
  /**
   * TRUE WHEN THE RUN IS ONE NAMED FIELD BEING EDITED, false when it is the
   * same thing simply happening again.
   *
   * The screen needs it to choose a word. "11 edits" is right above a
   * signatory list somebody worked through and wrong above eleven sign-ins,
   * which nobody edited.
   */
  oneField: boolean;
}

/**
 * Runs of one person editing one field, close together, become one line.
 *
 * The list arrives newest first and leaves newest first. A run is broken by a
 * different person, a different field, a different project, or a gap — and by
 * anything at all in between, because two edits with somebody else's action
 * between them are not one piece of work however close together they are.
 */
export function fold(entries: AuditEntry[], within = SAME_PIECE_OF_WORK): Folded[] {
  const out: Folded[] = [];
  let i = 0;
  while (i < entries.length) {
    const first = entries[i];
    const subject = subjectOf(first.what);
    /* The label when there is one, the whole sentence when there is not — see
       sameThingOf. Every run is built the same way; what differs is only what
       the line ends up saying. */
    const same = sameThingOf(first.what);
    const run = [first];
    let j = i + 1;
    while (j < entries.length) {
      const next = entries[j];
      /*
       * TWO RULES, BECAUSE THEY ARE TWO DIFFERENT THINGS.
       *
       * A field being worked through is bounded by a GAP — ten minutes from the
       * last keystroke, measured from the run's tail, because that is what "one
       * sitting" means. The same event happening again is bounded by the DAY,
       * anchored to the newest entry so a run can never chain past midnight.
       */
      const together = subject
        ? run[run.length - 1].at - next.at <= within
        : sameDay(first.at, next.at);
      if (
        next.who !== first.who ||
        next.kind !== first.kind ||
        next.project !== first.project ||
        sameThingOf(next.what) !== same ||
        !together
      ) {
        break;
      }
      run.push(next);
      j += 1;
    }
    i = j;

    /*
     * WHAT IT ENDED UP AS, which is the sentence the card asked for and is more
     * useful than the last line on its own. Newest first, so the run's last
     * element holds where it started and its first holds where it finished.
     *
     * Only when BOTH ends parse. A half-understood sentence rewritten with
     * confidence is worse than the original shown as it is.
     */
    const newest = endsOf(run[0].what);
    const oldest = endsOf(run[run.length - 1].what);
    const net = run.length > 1 && newest && oldest && subject
      ? `changed ${subject} from ${oldest.from} to ${newest.to}`
      : run[0].what;
    out.push({
      entry: run[0],
      what: net,
      times: run.length,
      behind: run,
      /* A reversal is the one thing folding could hide, so it is called out
         rather than smoothed over. Only a run of one FIELD can go nowhere —
         a sign-in that happened eleven times did not end where it started,
         it happened eleven times. */
      wentNowhere: run.length > 1 && Boolean(newest && oldest) && newest!.to === oldest!.from,
      oneField: Boolean(subject),
    });
  }
  return out;
}

/**
 * ── WHAT COUNTS AS SECURITY ─────────────────────────────────────────────────
 *
 * Asked for as "a security-only view — sign-ins, sign-outs, refusals,
 * permissions, credentials — kept apart from settings noise". These are the
 * kinds that answer *who could do what, and did they*. Everything left out —
 * `project`, `issue`, `comment` — is a record of work, not of access.
 *
 * `support` IS IN, and it is the one somebody might argue about. It is what
 * FlightDeck did in a customer's workspace: a support visit, a person added
 * from the operator portal, a password reissued. If a security view of an
 * insurance company's trail left out the things WE did in it, it would be the
 * one view where our own access is invisible.
 */
export const SECURITY_KINDS: AuditKind[] = [
  'signin',
  'session',
  'refused',
  'access',
  'role',
  'invite',
  'support',
];

export interface Sift {
  /** '' means every kind. */
  kind: string;
  /** '' means everybody. */
  who: string;
  /** Only the kinds that answer who could do what. */
  securityOnly: boolean;
  /** '' means every screen; otherwise the project (board) an entry concerned. */
  screen: string;
  /** 0 means all time; otherwise keep only entries at or after this moment (ms). */
  since: number;
}

export const NOTHING_SIFTED: Sift = { kind: '', who: '', securityOnly: false, screen: '', since: 0 };

export function sift(entries: AuditEntry[], f: Sift): AuditEntry[] {
  return entries.filter(
    (e) =>
      (!f.kind || e.kind === f.kind) &&
      (!f.who || e.who === f.who) &&
      (!f.securityOnly || SECURITY_KINDS.includes(e.kind as AuditKind)) &&
      (!f.screen || (e.project ?? '') === f.screen) &&
      (!f.since || e.at >= f.since)
  );
}

/**
 * WHAT A CHANGE ENTRY MOVED, FROM ONE VALUE TO ANOTHER — the change drill.
 *
 * The structured `from → to` comes from the sentence the app already writes, not
 * from a second store: every change line in the trail is generated by code in
 * one shape — "changed <field> from <before> to <after>" (a setting, an area or
 * sprint renamed, a date moved) — so the three parts are read back out of it.
 * The field is matched non-greedily and the FROM half likewise, so the first
 * " to " ends the before-value and the rest is the after; a line that is not a
 * "changed … from … to …" sentence (a role granted, a person added) has no
 * from→to and returns null, and the drill then shows only who, where and when.
 *
 * If a future increment writes the parts as structured columns on `audit.detail`
 * (more robust than a sentence to a wording change), they are preferred here.
 */
export interface ChangeFacts {
  field: string;
  from: string;
  to: string;
}

const CHANGE_RE = /^changed (.+?) from (.+?) to (.+)$/;

export function changeFacts(entry: AuditEntry): ChangeFacts | null {
  const d = entry.detail;
  if (d && d.field && (d.from !== undefined || d.to !== undefined)) {
    return { field: d.field, from: d.from ?? '', to: d.to ?? '' };
  }
  const m = CHANGE_RE.exec(entry.what);
  if (!m) return null;
  return { field: m[1], from: m[2], to: m[3] };
}

/**
 * THE TRAIL AS A FILE, because an auditor will ask for it as one.
 *
 * EVERY ROW, NEVER THE FOLDED LINES. The folding is a reading aid and this is
 * evidence; handing somebody 300 rows when the table holds 1,172 would be this
 * screen's convenience presented as their record. What is filtered out by kind
 * or by person is genuinely not wanted, and that is their choice — but a run
 * shown as one line still leaves as the entries it was written from.
 *
 * CSV because it opens in the thing they already have. The dates go out as ISO
 * 8601 rather than as "3 days ago", which is the format the screen is right to
 * use and a file is wrong to.
 */
export function asFile(
  entries: AuditEntry[],
  nameOf: (email: string) => string,
  originOf?: Map<string, AuditOrigin>,
): string {
  const quote = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  // Named for the reader, not the schema: Person is the display name, Email the
  // address, Screen the board the entry concerned. The sign-in parts (device,
  // system, browser, method, environment, new device) come straight from the
  // entry's structured detail, blank on the rows that have none. IP and Location
  // are the admin-only origin (0150), filled only for the admin who exported the
  // file and only on sign-in lines — a non-admin holds no origins, so the two
  // columns are present but empty. Summary keeps the plain sentence so the file
  // still reads without the structured columns.
  const head = [
    'When', 'Person', 'Email', 'Kind', 'Screen',
    'Target', 'Field', 'From', 'To',
    'Device', 'OS', 'Browser', 'Method', 'Environment', 'New device',
    'IP', 'Location',
    'Summary',
  ];
  const rows = entries.map((e) => {
    const d = e.detail ?? {};
    const c = changeFacts(e);
    const o = originOf?.get(e.id);
    return [
      new Date(e.at).toISOString(),
      nameOf(e.who),
      e.who,
      e.kind,
      e.project ?? '',
      c ? (e.project ?? 'Workspace') : '',
      c?.field ?? '',
      c?.from ?? '',
      c?.to ?? '',
      d.deviceKind ?? '',
      d.os ?? '',
      d.browser ?? '',
      d.method ?? '',
      d.environment ?? '',
      d.newDevice ? 'yes' : '',
      o?.ip ?? '',
      o ? originPlace(o) : '',
      e.what,
    ]
      .map(quote)
      .join(',');
  });
  return [head.map(quote).join(','), ...rows].join('\r\n');
}
